import type { TransformedDataPoint } from 'features/charts-v2/types/chart-strategy'

/**
 * Compute an ordinary least-squares linear fit over an indexed series.
 *
 * x = point index (0, 1, 2, …), y = the numeric value at that index.
 * Non-numeric / null / undefined values are excluded from the regression but
 * their position is preserved in the output array (null is returned for those
 * slots so recharts `connectNulls` can bridge over sparse data).
 *
 * Returns null for every slot when fewer than 2 valid data points exist.
 */
export function computeLeastSquaresFit(
  values: Array<number | null | undefined>,
): Array<number | null> {
  // Collect valid (x, y) pairs
  const pairs: Array<[number, number]> = []
  values.forEach((v, i) => {
    if (typeof v === 'number' && isFinite(v)) {
      pairs.push([i, v])
    }
  })

  if (pairs.length < 2) {
    return values.map(() => null)
  }

  const n = pairs.length
  const sumX = pairs.reduce((acc, [x]) => acc + x, 0)
  const sumY = pairs.reduce((acc, [, y]) => acc + y, 0)
  const sumXY = pairs.reduce((acc, [x, y]) => acc + x * y, 0)
  const sumX2 = pairs.reduce((acc, [x]) => acc + x * x, 0)

  const denom = n * sumX2 - sumX * sumX
  if (denom === 0) {
    // Degenerate case: all x values the same (shouldn't happen with indices)
    return values.map(() => null)
  }

  const slope = (n * sumXY - sumX * sumY) / denom
  const intercept = (sumY - slope * sumX) / n

  return values.map((_, i) => slope * i + intercept)
}

/**
 * Return a new chartData array that includes a `${key}__trend` field for each
 * requested value key. Existing fields are untouched.
 */
export function addTrendlineSeries(
  chartData: TransformedDataPoint[],
  valueKeys: string[],
): TransformedDataPoint[] {
  if (chartData.length === 0 || valueKeys.length === 0) return chartData

  const trendsByKey: Record<string, Array<number | null>> = {}
  for (const key of valueKeys) {
    const raw = chartData.map((row) => {
      const v = row[key]
      return typeof v === 'number' ? v : null
    })
    trendsByKey[key] = computeLeastSquaresFit(raw)
  }

  return chartData.map((row, i) => {
    const extra: Record<string, number | null> = {}
    for (const key of valueKeys) {
      extra[`${key}__trend`] = trendsByKey[key]![i] ?? null
    }
    return { ...row, ...extra }
  })
}
